import React, { useEffect } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Radio, Checkbox, Divider, Alert } from 'antd';
import dayjs from 'dayjs';
import { FieldGroupDTO, FieldMetadataDTO } from '../types';
import { useOnboardingStore } from '../stores/onboarding-store';
import DynamicSelect from '@/modules/customer-category/components/DynamicSelect';
import { UserSelect } from '@/modules/auth/components/UserSelect';
import FileUploadField from './FileUploadField';

const { TextArea } = Input;
const { Option } = Select;

interface SingleFieldGroupFormProps {
  fieldGroup: FieldGroupDTO;
  formData: Record<string, any>;
  onFieldChange: (fieldName: string, value: any) => void;
  errors?: Record<string, string>;
  readonly?: boolean;
  categoryId?: number; // Category ID for context in dynamic selects
}

const SingleFieldGroupForm: React.FC<SingleFieldGroupFormProps> = ({
  fieldGroup,
  formData,
  onFieldChange,
  errors = {},
  readonly = false,
  categoryId,
}) => {
  const [form] = Form.useForm();
  const { markStepCompleted, markStepIncomplete, currentStep } = useOnboardingStore();

  useEffect(() => {
    if (formData) {
      form.setFieldsValue(formData);
    }
  }, [formData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    onFieldChange(fieldName, value);
    form.setFieldsValue({ [fieldName]: value });
  };

  const validateFields = async () => {
    try {
      await form.validateFields();
      markStepCompleted(currentStep);
      return true;
    } catch (error) {
      markStepIncomplete(currentStep);
      return false;
    }
  };

  const renderField = (field: FieldMetadataDTO) => {
    if (!field.fieldName) return null;
    const fieldValue = formData[field.fieldName];
    const normalizedSelectValue = fieldValue === null || fieldValue === undefined ? undefined : String(fieldValue).trim();

    const commonProps = {
      placeholder: field.placeholder || `Enter ${field.label}`,
      disabled: readonly,
      onChange: (value: any) => handleFieldChange(field.fieldName!, value),
    };

    switch (field.type?.toLowerCase()) {
      case 'text':
      case 'string':
        return (
          <Input
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'email':
        return (
          <Input
            {...commonProps}
            type="email"
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'phone':
      case 'tel':
        return (
          <Input
            {...commonProps}
            type="tel"
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'number':
      case 'integer':
        return (
          <InputNumber
            {...commonProps}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.fieldName!, value)}
            style={{ width: '100%' }}
          />
        );

      case 'date':
        // Convert string dates to dayjs objects, ensuring validity
        let dateValue = null;
        if (fieldValue) {
          if (typeof fieldValue === 'string') {
            const parsed = dayjs(fieldValue);
            dateValue = parsed.isValid() ? parsed : null;
          } else if (dayjs.isDayjs(fieldValue)) {
            dateValue = fieldValue.isValid() ? fieldValue : null;
          }
        }
        
        return (
          <DatePicker
            {...commonProps}
            value={dateValue}
            onChange={(date) => handleFieldChange(field.fieldName!, date)}
            style={{ width: '100%' }}
          />
        );

      case 'textarea':
        return (
          <TextArea
            {...commonProps}
            rows={4}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );

      case 'select':
        // Use DynamicSelect if field has selectProvider, otherwise use regular Select
        if (field.selectProvider) {
          return (
            <DynamicSelect
              providerName={field.selectProvider}
              fieldName={field.fieldName!}
              entityType="customer" // Context for the select field
              categoryId={categoryId} // Pass category ID for context
              value={normalizedSelectValue}
              onChange={(value) => handleFieldChange(field.fieldName!, value)}
              placeholder={field.placeholder || `Select ${field.label}`}
              disabled={readonly}
              style={{ width: '100%' }}
              showDescriptions={true}
              groupOptions={field.selectProvider === 'education_level'} // Enable grouping for education_level
            />
          );
        }
        
        // Fallback to regular Select for fields without selectProvider
        return (
          <Select
            {...commonProps}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.fieldName!, value)}
            placeholder={field.placeholder || `Select ${field.label}`}
          >
            {/* Options would need to be provided via field.selectOptions */}
            <Option value="">Select an option</Option>
          </Select>
        );

      case 'user':
        return (
          <UserSelect
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.fieldName!, value)}
            placeholder={field.placeholder || `Select ${field.label}`}
            style={{ width: '100%' }}
            disabled={readonly}
          />
        );

      case 'radio':
        return (
          <Radio.Group
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox
            {...commonProps}
            checked={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.checked)}
          >
            {field.label}
          </Checkbox>
        );

      case 'file':
        return (
          <FileUploadField
            fieldName={field.fieldName!}
            label={field.label ?? ''}
            value={fieldValue}
            onChange={(fileId, uploadedAt) => {
              handleFieldChange(field.fieldName!, fileId);
              // Also store the upload timestamp if needed
              if (uploadedAt && field.fieldName!.includes('FileId')) {
                const timestampField = field.fieldName!.replace('FileId', 'UploadedAt');
                handleFieldChange(timestampField, uploadedAt);
              }
            }}
            placeholder={field.placeholder || `Upload ${field.label}`}
            disabled={readonly}
            accept="image/*"
            maxSize={5}
          />
        );

      default:
        return (
          <Input
            {...commonProps}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.fieldName!, e.target.value)}
          />
        );
    }
  };

  if (!fieldGroup.fields || fieldGroup.fields.length === 0) {
    return (
      <Alert
        message="No Fields Available"
        description="This step has no fields configured."
        type="info"
        showIcon
      />
    );
  }

  const sortedFields = [...fieldGroup.fields]
    .filter(field => field.isVisible)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return (
    <div className="single-field-group-form">
      <div className="field-group">
        <Divider orientation="left" className="group-divider">
          <span className="group-title">
            {fieldGroup.icon && <span className="group-icon">{fieldGroup.icon}</span>}
            {fieldGroup.groupName}
          </span>
        </Divider>
        
        {fieldGroup.description && (
          <p className="group-description">{fieldGroup.description}</p>
        )}

        <Form
          form={form}
          layout="vertical"
          onValuesChange={validateFields}
          className="onboarding-form"
        >
          <div className="group-fields">
            {sortedFields.map((field) => (
              <Form.Item
                key={field.fieldName}
                name={field.fieldName}
                label={field.label}
                rules={[
                  {
                    required: field.defaultRequired,
                    message: `${field.label} is required`,
                  },
                ]}
                help={field.helpText}
                validateStatus={field.fieldName && errors[field.fieldName] ? 'error' : ''}
                hasFeedback
              >
                {renderField(field)}
              </Form.Item>
            ))}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SingleFieldGroupForm;
